import { Component, ErrorInfo, ReactNode } from 'react';

import { logError } from '../../analytics/analytics';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  message: string;
  showReloadButton: boolean;
}

const MAINTENANCE_MESSAGES = [
  "The grounds crew is top-dressing the greens. Fresh features appearing shortly!",
  "We're out doing some course maintenance. We'll have you back on the tee in a moment.",
  "The crew is out marking the hazards. New updates rolling onto the course now!",
  "We're aerating the fairways for better performance. Back in a flash!",
];

const ERROR_MESSAGES = [
  "Our bad—whoever designed this bunker didn't rake it properly. We're smoothing it out now; please try again.",
  "We missed a hazard on the map! That's on us. We're clearing the path—give it another go.",
  "Looks like we built a sand trap where there shouldn't be one. We're redesigning that section—try your shot again.",
  "A bunker popped up where it shouldn't have. Our crew is fixing the layout; please bear with us.",
];

const ANIMATION_DURATION_MS = 5000;
const AUTO_RELOAD_MS = 2000;

function pick(messages: string[]): string {
  return messages[Math.floor(Math.random() * messages.length)];
}

function isChunkLoadError(error: Error): boolean {
  return (
    error.message.includes('dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.name === 'ChunkLoadError'
  );
}

class ErrorBoundary extends Component<Props, State> {
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;
  private buttonTimer: ReturnType<typeof setTimeout> | null = null;

  state: State = { hasError: false, isChunkError: false, message: '', showReloadButton: false };

  static getDerivedStateFromError(error: Error): State {
    const isChunk = isChunkLoadError(error);
    return {
      hasError: true,
      isChunkError: isChunk,
      message: isChunk ? pick(MAINTENANCE_MESSAGES) : pick(ERROR_MESSAGES),
      showReloadButton: false,
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logError('ErrorBoundary', {
      message: error.message,
      componentStack: info.componentStack?.slice(0, 100),
    });
    if (isChunkLoadError(error)) {
      this.reloadTimer = setTimeout(() => window.location.reload(), AUTO_RELOAD_MS);
    }
    this.buttonTimer = setTimeout(() => this.setState({ showReloadButton: true }), ANIMATION_DURATION_MS);
  }

  componentWillUnmount(): void {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
    if (this.buttonTimer) clearTimeout(this.buttonTimer);
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const branding = (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <img src="/icon.svg" alt="Gratigolf" style={{ width: '2rem', height: '2rem' }} />
        <span style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '1.25rem',
          fontWeight: 400,
          letterSpacing: '0.00625rem',
          color: '#333',
        }}>
          Gratigolf
        </span>
      </div>
    );

    const message = (
      <p style={{
        fontSize: '1.25rem',
        textAlign: 'center',
        maxWidth: '32rem',
        margin: 0,
        color: this.state.isChunkError ? '#558b2f' : '#7a5c1e',
      }}>
        {this.state.message}
      </p>
    );

    if (this.state.isChunkError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif',
          backgroundColor: '#fff', gap: '2rem', padding: '2rem',
        }}>
          <style>{`
            @keyframes mow-move {
              from { left: 0; }
              to   { left: calc(100% - 6rem); }
            }
            @keyframes mow-wheelie {
              0%   { transform: rotate(0deg); }
              35%  { transform: rotate(0deg); }
              45%  { transform: rotate(-22deg); }
              62%  { transform: rotate(-22deg); }
              72%  { transform: rotate(0deg); }
              100% { transform: rotate(0deg); }
            }
          `}</style>
          {branding}
          {message}
          <div style={{ width: '100%', height: '6rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              height: '1.5rem', backgroundColor: '#4caf50',
            }} />
            <span style={{
              position: 'absolute', bottom: '1.5rem',
              transformOrigin: '20% 100%',
              animation: `mow-move ${ANIMATION_DURATION_MS}ms linear forwards, mow-wheelie ${ANIMATION_DURATION_MS}ms ease-in-out forwards`,
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 110 70" height="3.5rem" style={{ display: 'block' }}>
                <circle cx="22" cy="54" r="16" fill="#2c2c2c"/>
                <circle cx="22" cy="54" r="7" fill="#666"/>
                <circle cx="94" cy="58" r="10" fill="#2c2c2c"/>
                <circle cx="94" cy="58" r="4" fill="#666"/>
                <rect x="12" y="40" width="90" height="18" rx="4" fill="#f5a623"/>
                <rect x="58" y="27" width="40" height="16" rx="3" fill="#d4920a"/>
                <rect x="36" y="32" width="20" height="10" rx="3" fill="#8B4513"/>
                <rect x="62" y="20" width="3" height="14" fill="#444"/>
                <ellipse cx="63" cy="19" rx="8" ry="3.5" fill="none" stroke="#444" strokeWidth="2.5"/>
                <circle cx="44" cy="16" r="8" fill="#fad7a0"/>
                <ellipse cx="44" cy="11" rx="8" ry="5" fill="#c0392b"/>
                <path d="M37 14 Q26 10 28 20" stroke="#c0392b" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
                <path d="M36 24 L44 21 L52 24 L50 33 L38 33 Z" fill="#3498db"/>
                <line x1="50" y1="27" x2="63" y2="21" stroke="#fad7a0" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="38" y1="27" x2="32" y2="33" stroke="#fad7a0" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </span>
          </div>
          {this.state.showReloadButton && (
            <button onClick={() => window.location.reload()} style={{
              padding: '0.5rem 1.5rem', fontSize: '1rem', fontFamily: 'Inter, sans-serif',
              backgroundColor: '#4caf50', color: '#fff', border: 'none',
              borderRadius: '0.5rem', cursor: 'pointer',
            }}>
              Reload
            </button>
          )}
        </div>
      );
    }

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif',
        backgroundColor: '#fff', gap: '2rem', padding: '2rem',
      }}>
        <style>{`
          @keyframes ball-travel {
            0%   { left: -1.5rem; }
            65%  { left: 68%; animation-timing-function: ease-out; }
            100% { left: 72%; }
          }
          @keyframes ball-bounce {
            /* Initial fall — clustered keyframes simulate gravitational acceleration */
            0%    { transform: translateY(-3.5rem) scaleX(1) scaleY(1); }
            3%    { transform: translateY(-2rem) scaleX(1) scaleY(1); }
            6%    { transform: translateY(-0.9rem) scaleX(1) scaleY(1); }
            9%    { transform: translateY(-0.2rem) scaleX(1) scaleY(1); }
            /* 1st contact — squish, then spring back */
            12%   { transform: translateY(0) scaleX(1.2) scaleY(0.7); }
            13.5% { transform: translateY(0) scaleX(1) scaleY(1); }
            /* Bounce 1: rises to 1.5rem, parabolic (slow near peak, fast near ground) */
            18%   { transform: translateY(-1.1rem) scaleX(1) scaleY(1); }
            23%   { transform: translateY(-1.5rem) scaleX(1) scaleY(1); }
            29%   { transform: translateY(-1.1rem) scaleX(1) scaleY(1); }
            33%   { transform: translateY(-0.2rem) scaleX(1) scaleY(1); }
            /* 2nd contact — lighter squish (less energy) */
            35%   { transform: translateY(0) scaleX(1.15) scaleY(0.78); }
            36.5% { transform: translateY(0) scaleX(1) scaleY(1); }
            /* Bounce 2: rises to 0.6rem */
            38.5% { transform: translateY(-0.45rem) scaleX(1) scaleY(1); }
            42%   { transform: translateY(-0.6rem) scaleX(1) scaleY(1); }
            45.5% { transform: translateY(-0.45rem) scaleX(1) scaleY(1); }
            48%   { transform: translateY(-0.1rem) scaleX(1) scaleY(1); }
            /* 3rd contact */
            49%   { transform: translateY(0) scaleX(1.1) scaleY(0.85); }
            50%   { transform: translateY(0) scaleX(1) scaleY(1); }
            /* Bounce 3: rises to 0.25rem */
            51.5% { transform: translateY(-0.19rem) scaleX(1) scaleY(1); }
            54%   { transform: translateY(-0.25rem) scaleX(1) scaleY(1); }
            56.5% { transform: translateY(-0.19rem) scaleX(1) scaleY(1); }
            58%   { transform: translateY(-0.05rem) scaleX(1) scaleY(1); }
            /* 4th contact — almost no squish */
            59%   { transform: translateY(0) scaleX(1.06) scaleY(0.92); }
            59.8% { transform: translateY(0) scaleX(1) scaleY(1); }
            /* Tiny last hop, then sinks into sand */
            62%   { transform: translateY(-0.1rem) scaleX(1) scaleY(1); }
            65%   { transform: translateY(0.4rem) scaleX(1) scaleY(1); }
            100%  { transform: translateY(0.4rem) scaleX(1) scaleY(1); }
          }
        `}</style>
        {branding}
        {message}
        <div style={{ width: '100%', height: '6rem', position: 'relative', overflow: 'visible' }}>
          {/* Grass — left 2/3 */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, width: '67%',
            height: '1.5rem', backgroundColor: '#4caf50',
          }} />
          {/* Sand — right 1/3 */}
          <div style={{
            position: 'absolute', bottom: 0, left: '67%', right: 0,
            height: '1.5rem', backgroundColor: '#f0dda8',
          }} />
          {/* Golf ball */}
          <span style={{
            position: 'absolute',
            bottom: '1.5rem',
            transformOrigin: '50% 100%',
            animation: `ball-travel ${ANIMATION_DURATION_MS}ms linear forwards, ball-bounce ${ANIMATION_DURATION_MS}ms linear forwards`,
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="1.4rem" height="1.4rem" style={{ display: 'block' }}>
              <defs>
                <radialGradient id="ballGrad" cx="36%" cy="28%" r="64%">
                  <stop offset="0%" stopColor="#ffffff"/>
                  <stop offset="55%" stopColor="#f5f5f5"/>
                  <stop offset="100%" stopColor="#d0d0d0"/>
                </radialGradient>
                <clipPath id="ballClip">
                  <circle cx="25" cy="25" r="22"/>
                </clipPath>
              </defs>
              {/* Sphere body */}
              <circle cx="25" cy="25" r="22.5" fill="url(#ballGrad)" stroke="#ccc" strokeWidth="0.8"/>
              {/* Dimples */}
              <g clipPath="url(#ballClip)" fill="#ebebeb" stroke="#ccc" strokeWidth="0.6">
                <circle cx="20" cy="11" r="3"/>
                <circle cx="30" cy="11" r="3"/>
                <circle cx="13" cy="18" r="3"/>
                <circle cx="23" cy="17" r="3"/>
                <circle cx="33" cy="17" r="3"/>
                <circle cx="40" cy="20" r="3"/>
                <circle cx="10" cy="26" r="3"/>
                <circle cx="19" cy="25" r="3"/>
                <circle cx="28" cy="24" r="3"/>
                <circle cx="37" cy="26" r="3"/>
                <circle cx="14" cy="33" r="3"/>
                <circle cx="24" cy="33" r="3"/>
                <circle cx="34" cy="33" r="3"/>
                <circle cx="40" cy="33" r="3"/>
                <circle cx="19" cy="40" r="3"/>
                <circle cx="29" cy="40" r="3"/>
              </g>
              {/* Specular highlight */}
              <ellipse cx="17" cy="15" rx="5.5" ry="3.5" fill="white" opacity="0.7" transform="rotate(-25 17 15)"/>
            </svg>
          </span>
        </div>
        {this.state.showReloadButton && (
          <button onClick={() => window.location.reload()} style={{
            padding: '0.5rem 1.5rem', fontSize: '1rem', fontFamily: 'Inter, sans-serif',
            backgroundColor: '#d4920a', color: '#fff', border: 'none',
            borderRadius: '0.5rem', cursor: 'pointer',
          }}>
            Try Again
          </button>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
